// DataTableWithExport.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, toastError } from "../../utils/util";
import useApiEp from "../../hooks/useApiEp";
import { toast } from "react-toastify";
import CustomCircularP from "../../widgets/CustomCircularP";
import { useParams } from "react-router-dom";
import BackBtn from "../../widgets/BackBtn";

const DataTableWithExport = () => {
  const { id } = useParams();
  const [rows, setRows] = useState([]);
  const [perRow, setPerRow] = useState(25);
  const [dynamicKeys, setDynamicKeys] = useState([]);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError, error } = useApiEp(
    `/message/get-flow-response?page=${page}&campaign=${id}&limit=${perRow}`
  );

  useEffect(() => {
    if (data) {
      const fetchedData = data.data || [];
      // Map the data to rename flow_token to campaignName
      const renamedData = fetchedData.map((item) => ({
        ...item,
        ...(item.data && {
          data: {
            ...item.data,
            ...(item.data.flow_token && { campaignName: item.data.flow_token }),
            ...(item.data.flow_token && { flow_token: undefined }), // Optionally remove the original flow_token
          },
        }),
      }));
      const keys = new Set();
      renamedData.forEach((item) => {
        Object.keys(item.data || {}).forEach((k) =>
          keys.add(k === "flow_token" ? "campaignName" : k)
        );
      });
      setRows(renamedData);
      setDynamicKeys(Array.from(keys));
    }
  }, [data]);

  const handleExport = async () => {
    if (exporting) {
      return;
    }
    try {
      setExporting(true);
      const response = await axios.get(
        `${API_ENDPOINT}/message/get-flow-response-export?campaign=${id}`,
        getHeader()
      );
      const exportData = response.data.map((item) => ({
        profileName: item.profileName,
        from: item.from,
        ...item.data,
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
      XLSX.writeFile(workbook, "exported_data.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ mt: 3 }}>
      <BackBtn />
      <CustomCircularP show={exporting} />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Flows Responses of {id}</Typography>
        <Button variant="contained" onClick={handleExport}>
          Export to Excel
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Si.no</strong>
              </TableCell>
              <TableCell>
                <strong>Profile Name</strong>
              </TableCell>
              <TableCell>
                <strong>From (Mobile)</strong>
              </TableCell>
              {dynamicKeys.map((key) => (
                <TableCell key={key}>
                  <strong>{key}</strong>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{perRow * (page - 1) + index + 1}</TableCell>
                <TableCell>{row.profileName}</TableCell>
                <TableCell>{row.from}</TableCell>
                {dynamicKeys.map((key) => (
                  <TableCell key={key}>{row.data?.[key] ?? "--"}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display={"flex"} justifyContent={"center"} my={2}>
        <Pagination
          count={data?.totalPages}
          page={page}
          onChange={(e, page) => {
            setPage(page);
          }}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>
    </Container>
  );
};

export default DataTableWithExport;
